import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { groupBy } from 'lodash';
import clsx from 'clsx';

import { useCart } from '../../../../common';
import useGTM, { Events } from '../../../../../common/hooks/useGTM';
import Size from './Size';

import './styles.less';

const Options = props => {
  const { product } = props;
  const { options } = product;
  const [selectedColor, setSelectedColor] = useState();
  const { items, updateCart } = useCart();
  const { pushEvent } = useGTM();

  const groupedOptions = useMemo(
    () => {
      const grouped = groupBy(options, _ => _.colorCode);
      return Object
        .keys(grouped)
        .reduce((acc, key) => ({
          ...acc,
          [key]: groupBy(grouped[key], _ => _.size)
        }), {});
    },
    [options]
  );

  const sizeOptions = useMemo(
    () => {

      if (selectedColor) {
        const cartItem = items.find(_ => _.product.id === product.id);

        return Object
          .keys(groupedOptions[selectedColor])
          .map(size => {
            const sizes = groupedOptions[selectedColor][size];
            let available = sizes;

            if (cartItem) {
              const selectedIds = cartItem.options.map(_ => _.id);
              available = sizes.filter(_ => !selectedIds.includes(_.id));
            }

            return {
              size,
              available
            };
          });
      }

      return [];
    },
    [groupedOptions, selectedColor, items, product.id]
  );

  const handleColorSelect = useCallback(
    color => () => {
      setSelectedColor(color);
    },
    [setSelectedColor]
  );

  const handleOnSizeClick = useCallback(
    (option, ref) => {
      const productItem = items
        .find(_ => _.product.id === product.id);

      if (!productItem) {
        pushEvent(
          Events
            .productsCategory
            .addToCartClick({ label: `product_${product.id}` })
        );

        return updateCart([
          ...items,
          { product, options: [option], ref }
        ]);
      }

      const isAdded = productItem.options
        .map(_ => _.id)
        .includes(option.id);

      if (isAdded) return;

      return updateCart([
        ...items.filter(_ => _.product.id !== product.id),
        { product, options: [...productItem.options, option], ref }
      ]);
    },
    [items, pushEvent, product, updateCart]
  );

  useEffect(() => {
    if (groupedOptions && !selectedColor) {
      const firstColor = Object.keys(groupedOptions)[0];
      firstColor && handleColorSelect(firstColor)();
    }
  // eslint-disable-next-line
  }, [groupedOptions]);

  return (
    <div className="options-container">
      <div>
        {
          Object
            .keys(groupedOptions)
            .map(color =>
              <div
                key={color}
                onClick={handleColorSelect(color)}
                className={clsx(
                  'color-item',
                  selectedColor === color && 'active'
                )}
                style={{ backgroundColor: color }}
              />
            )
        }
      </div>
      <div className="sizes-container">
        {
          sizeOptions
            .map(({ size, available }) =>
              <Size
                key={size}
                size={size}
                available={available}
                onClick={handleOnSizeClick}
              />
            )
        }
      </div>
    </div>
  );
};

Options.propTypes = {
  product: PropTypes.object.isRequired
};

export default Options;